import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Palette, RotateCcw, Save, ChevronDown, ChevronRight, Upload, X, Image } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { resolveFileUrl } from '@/lib/utils';

const BRAND_BLUE = '#2B4B6A';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Color Picker Component
const ColorPicker = ({ label, value, onChange }) => {
  return (
    <div className="flex items-center justify-between py-2">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer"
        />
        <input
          type="text"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="w-24 px-2 py-1.5 text-sm border border-gray-200 rounded-lg font-mono"
          placeholder="#000000"
        />
      </div>
    </div>
  );
};

// Size Input Component
const SizeInput = ({ label, value, onChange, options = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px'] }) => {
  return (
    <div className="flex items-center justify-between py-2">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <select
        value={value || '16px'}
        onChange={(e) => onChange(e.target.value)}
        className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg"
      >
        {options.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );
};

// Pixel Input Component - allows manual text input for pixel values
const PixelInput = ({ label, value, onChange, placeholder = '200' }) => {
  // Extract numeric value from stored value (e.g., '200px' -> '200')
  const numericValue = value ? value.replace('px', '') : '';
  
  const handleChange = (e) => {
    const num = e.target.value.replace(/[^0-9]/g, ''); // Only allow numbers
    onChange(num ? `${num}px` : '');
  };
  
  return (
    <div className="flex items-center justify-between py-2">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={numericValue}
          onChange={handleChange}
          placeholder={placeholder}
          className="w-20 px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-right"
        />
        <span className="text-sm text-gray-500">px</span>
      </div>
    </div>
  );
};

// Logo Upload Component
const LogoUpload = ({ currentLogoUrl, onLogoChange }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentLogoUrl);

  useEffect(() => {
    setPreviewUrl(currentLogoUrl);
  }, [currentLogoUrl]);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Don't set Content-Type header - let axios set it with proper boundary
      const res = await api.post('/uploads/logo', formData);
      
      const newLogoUrl = res.data.logo_url;
      setPreviewUrl(newLogoUrl);
      onLogoChange(newLogoUrl);
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = () => {
    setPreviewUrl('');
    onLogoChange('');
  };

  return (
    <div className="py-3 space-y-4">
      <label className="block text-sm font-medium text-gray-700 mb-3">Site Logo</label>
      
      <div className="flex items-start gap-4">
        {/* Logo Preview */}
        <div 
          className="w-56 h-20 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center overflow-hidden p-3"
          style={{ backgroundColor: '#2B4B6A' }}
        >
          {previewUrl ? (
            <img 
              src={resolveFileUrl(previewUrl, API_URL)}
              alt="Logo preview" 
              className="w-full h-auto object-contain"
            />
          ) : (
            <div className="text-center text-white/50">
              <Image className="w-8 h-8 mx-auto mb-1" />
              <span className="text-xs">No logo</span>
            </div>
          )}
        </div>

        {/* Upload Controls */}
        <div className="flex-1">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*"
            className="hidden"
          />
          
          <div className="flex flex-col gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg transition flex items-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: BRAND_BLUE }}
            >
              <Upload className="w-4 h-4" />
              {uploading ? 'Uploading...' : 'Upload Logo'}
            </button>
            
            {previewUrl && (
              <button
                onClick={handleRemoveLogo}
                className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Remove Logo
              </button>
            )}
          </div>
          
          <p className="text-xs text-gray-500 mt-2">
            Recommended: PNG or SVG with transparent background. Logo will fill sidebar width.
          </p>
        </div>
      </div>
    </div>
  );
};

// Section Component
const Section = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-5 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition"
      >
        <span className="font-semibold text-gray-800">{title}</span>
        {isOpen ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
      </button>
      {isOpen && (
        <div className="px-5 py-4 bg-white divide-y divide-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

const AdminThemeSettings = () => {
  const { adminTheme, userTheme, refreshTheme, defaultAdminTheme, defaultUserTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('admin'); // 'admin' or 'user'
  const [adminSettings, setAdminSettings] = useState(null);
  const [userSettings, setUserSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    fetchThemeSettings();
  }, []);

  const fetchThemeSettings = async () => {
    try {
      const res = await api.get('/admin/theme');
      setAdminSettings(res.data.admin_theme || defaultAdminTheme);
      setUserSettings(res.data.user_theme || defaultUserTheme);
    } catch (error) {
      toast.error('Failed to load theme settings');
      setAdminSettings(defaultAdminTheme);
      setUserSettings(defaultUserTheme);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/theme', {
        admin_theme: adminSettings,
        user_theme: userSettings
      });
      toast.success('Theme settings saved successfully');
      refreshTheme();
    } catch (error) {
      toast.error('Failed to save theme settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset all theme settings to defaults?')) return;
    
    try {
      await api.post('/admin/theme/reset');
      setAdminSettings(defaultAdminTheme);
      setUserSettings(defaultUserTheme);
      toast.success('Theme reset to defaults');
      refreshTheme();
    } catch (error) {
      toast.error('Failed to reset theme');
    }
  };

  const updateAdminSetting = (section, key, value) => {
    setAdminSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
  };

  const updateUserSetting = (section, key, value) => {
    setUserSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300" style={{ borderTopColor: BRAND_BLUE }}></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl" style={{ backgroundColor: BRAND_BLUE }}>
              <Palette className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Theme Settings</h1>
              <p className="text-gray-500 text-sm mt-1">Customize the appearance of admin and user interfaces</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleReset}
              className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 transition flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Reset to Defaults
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2.5 text-white rounded-xl transition flex items-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: BRAND_BLUE }}
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('admin')}
            className={`px-5 py-2.5 rounded-xl font-medium transition ${
              activeTab === 'admin' 
                ? 'text-white' 
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
            style={activeTab === 'admin' ? { backgroundColor: BRAND_BLUE } : {}}
          >
            Admin Interface
          </button>
          <button
            onClick={() => setActiveTab('user')}
            className={`px-5 py-2.5 rounded-xl font-medium transition ${
              activeTab === 'user' 
                ? 'text-white' 
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
            style={activeTab === 'user' ? { backgroundColor: BRAND_BLUE } : {}}
          >
            User Interface
          </button>
        </div>

        {/* Admin Theme Settings */}
        {activeTab === 'admin' && adminSettings && (
          <div className="space-y-4">
            {/* Logo Upload - Separate Section at Top */}
            <Section title="Site Logo & Title" defaultOpen={true}>
              <LogoUpload
                currentLogoUrl={adminSettings.sidebar?.logoUrl}
                onLogoChange={(url) => {
                  updateAdminSetting('sidebar', 'logoUrl', url);
                  updateUserSetting('sidebar', 'logoUrl', url);
                  refreshTheme();
                }}
              />
              
              {/* Site Title */}
              <div className="pt-4 mt-4 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">Site Title</label>
                <input
                  type="text"
                  value={adminSettings.sidebar?.siteTitle || ''}
                  onChange={(e) => {
                    updateAdminSetting('sidebar', 'siteTitle', e.target.value);
                    updateUserSetting('sidebar', 'siteTitle', e.target.value);
                  }}
                  placeholder="Trados — The Trusted Trade Platform"
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': BRAND_BLUE }}
                />
                <p className="text-xs text-gray-500 mt-2">
                  This title appears in the footer and copyright text on public pages.
                </p>
              </div>

              {/* Footer Powered By Settings */}
              <div className="pt-4 mt-4 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">Footer Message</label>
                <input
                  type="text"
                  value={adminSettings.footer?.poweredByText || ''}
                  onChange={(e) => updateAdminSetting('footer', 'poweredByText', e.target.value)}
                  placeholder="Powered by Trados"
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': BRAND_BLUE }}
                />
                <p className="text-xs text-gray-500 mt-2">
                  This message appears at the bottom right of all pages. Leave empty to hide.
                </p>
              </div>

              <div className="pt-4 mt-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Footer Link URL</label>
                <input
                  type="url"
                  value={adminSettings.footer?.poweredByUrl || ''}
                  onChange={(e) => updateAdminSetting('footer', 'poweredByUrl', e.target.value)}
                  placeholder="https://www.trados.co"
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': BRAND_BLUE }}
                />
                <p className="text-xs text-gray-500 mt-2">
                  URL the footer message links to. Opens in a new tab.
                </p>
              </div>
            </Section>

            {/* Sidebar */}
            <Section title="Sidebar Colors">
              <ColorPicker
                label="Background Color"
                value={adminSettings.sidebar?.backgroundColor}
                onChange={(v) => updateAdminSetting('sidebar', 'backgroundColor', v)}
              />
              <ColorPicker
                label="Text Color"
                value={adminSettings.sidebar?.textColor}
                onChange={(v) => updateAdminSetting('sidebar', 'textColor', v)}
              />
              <ColorPicker
                label="Hover Color"
                value={adminSettings.sidebar?.hoverColor}
                onChange={(v) => updateAdminSetting('sidebar', 'hoverColor', v)}
              />
              <ColorPicker
                label="Active Item Color"
                value={adminSettings.sidebar?.activeColor}
                onChange={(v) => updateAdminSetting('sidebar', 'activeColor', v)}
              />
            </Section>

            {/* Page */}
            <Section title="Page Background & Typography">
              <ColorPicker
                label="Page Background Color"
                value={adminSettings.page?.backgroundColor}
                onChange={(v) => updateAdminSetting('page', 'backgroundColor', v)}
              />
              <ColorPicker
                label="Text Color"
                value={adminSettings.page?.textColor}
                onChange={(v) => updateAdminSetting('page', 'textColor', v)}
              />
              <ColorPicker
                label="Page Title Color"
                value={adminSettings.page?.titleColor}
                onChange={(v) => updateAdminSetting('page', 'titleColor', v)}
              />
              <SizeInput
                label="Title Size"
                value={adminSettings.page?.titleSize}
                onChange={(v) => updateAdminSetting('page', 'titleSize', v)}
              />
            </Section>

            {/* External Pages (Login, Register, Home) */}
            <Section title="External Pages (Login, Register, Home)">
              <ColorPicker
                label="Top Background Color"
                value={adminSettings.externalPages?.topBackgroundColor}
                onChange={(v) => updateAdminSetting('externalPages', 'topBackgroundColor', v)}
              />
              <ColorPicker
                label="Page Background Color"
                value={adminSettings.externalPages?.pageBackgroundColor}
                onChange={(v) => updateAdminSetting('externalPages', 'pageBackgroundColor', v)}
              />
              <p className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100">
                These colors apply to public pages: Login, Registration, and Home page.
              </p>
            </Section>

            {/* Header */}
            <Section title="Header">
              <ColorPicker
                label="Background Color"
                value={adminSettings.header?.backgroundColor}
                onChange={(v) => updateAdminSetting('header', 'backgroundColor', v)}
              />
              <ColorPicker
                label="Text Color"
                value={adminSettings.header?.textColor}
                onChange={(v) => updateAdminSetting('header', 'textColor', v)}
              />
            </Section>

            {/* Cards */}
            <Section title="Cards & Panels">
              <ColorPicker
                label="Card Background Color"
                value={adminSettings.cards?.backgroundColor}
                onChange={(v) => updateAdminSetting('cards', 'backgroundColor', v)}
              />
              <ColorPicker
                label="Card Border Color"
                value={adminSettings.cards?.borderColor}
                onChange={(v) => updateAdminSetting('cards', 'borderColor', v)}
              />
              <ColorPicker
                label="Card Text Color"
                value={adminSettings.cards?.textColor}
                onChange={(v) => updateAdminSetting('cards', 'textColor', v)}
              />
              <ColorPicker
                label="Card Header Color"
                value={adminSettings.cards?.headerColor}
                onChange={(v) => updateAdminSetting('cards', 'headerColor', v)}
              />
            </Section>

            {/* Buttons */}
            <Section title="Buttons">
              <ColorPicker
                label="Primary Button Color"
                value={adminSettings.buttons?.primaryColor}
                onChange={(v) => updateAdminSetting('buttons', 'primaryColor', v)}
              />
              <ColorPicker
                label="Primary Button Text Color"
                value={adminSettings.buttons?.primaryTextColor}
                onChange={(v) => updateAdminSetting('buttons', 'primaryTextColor', v)}
              />
              <ColorPicker
                label="Secondary Button Color"
                value={adminSettings.buttons?.secondaryColor}
                onChange={(v) => updateAdminSetting('buttons', 'secondaryColor', v)}
              />
              <ColorPicker
                label="Secondary Button Text Color"
                value={adminSettings.buttons?.secondaryTextColor}
                onChange={(v) => updateAdminSetting('buttons', 'secondaryTextColor', v)}
              />
            </Section>

            {/* Tables */}
            <Section title="Tables">
              <ColorPicker
                label="Header Background Color"
                value={adminSettings.tables?.headerBackgroundColor}
                onChange={(v) => updateAdminSetting('tables', 'headerBackgroundColor', v)}
              />
              <ColorPicker
                label="Header Text Color"
                value={adminSettings.tables?.headerTextColor}
                onChange={(v) => updateAdminSetting('tables', 'headerTextColor', v)}
              />
              <ColorPicker
                label="Row Background Color"
                value={adminSettings.tables?.rowBackgroundColor}
                onChange={(v) => updateAdminSetting('tables', 'rowBackgroundColor', v)}
              />
              <ColorPicker
                label="Row Text Color"
                value={adminSettings.tables?.rowTextColor}
                onChange={(v) => updateAdminSetting('tables', 'rowTextColor', v)}
              />
              <ColorPicker
                label="Border Color"
                value={adminSettings.tables?.borderColor}
                onChange={(v) => updateAdminSetting('tables', 'borderColor', v)}
              />
            </Section>

            {/* Filters */}
            <Section title="Filters & Search">
              <ColorPicker
                label="Background Color"
                value={adminSettings.filters?.backgroundColor || '#D4F6FF'}
                onChange={(v) => updateAdminSetting('filters', 'backgroundColor', v)}
              />
              <PixelInput
                label="Dropdown Max Width (Closed)"
                value={adminSettings.filters?.maxClosedWidth || '200px'}
                onChange={(v) => updateAdminSetting('filters', 'maxClosedWidth', v)}
                placeholder="200"
              />
              <PixelInput
                label="Dropdown Max Width (Open)"
                value={adminSettings.filters?.maxOpenWidth || '300px'}
                onChange={(v) => updateAdminSetting('filters', 'maxOpenWidth', v)}
                placeholder="300"
              />
            </Section>
          </div>
        )}

        {/* User Theme Settings */}
        {activeTab === 'user' && userSettings && (
          <div className="space-y-4">
            {/* Note about logo */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-blue-700">
                <strong>Note:</strong> Site logo is configured in the Admin Interface tab and applies to both admin and user interfaces.
              </p>
            </div>

            {/* Sidebar */}
            <Section title="Sidebar Colors" defaultOpen={true}>
              <ColorPicker
                label="Background Color"
                value={userSettings.sidebar?.backgroundColor}
                onChange={(v) => updateUserSetting('sidebar', 'backgroundColor', v)}
              />
              <ColorPicker
                label="Text Color"
                value={userSettings.sidebar?.textColor}
                onChange={(v) => updateUserSetting('sidebar', 'textColor', v)}
              />
              <ColorPicker
                label="Hover Color"
                value={userSettings.sidebar?.hoverColor}
                onChange={(v) => updateUserSetting('sidebar', 'hoverColor', v)}
              />
              <ColorPicker
                label="Active Item Color"
                value={userSettings.sidebar?.activeColor}
                onChange={(v) => updateUserSetting('sidebar', 'activeColor', v)}
              />
            </Section>

            {/* Page */}
            <Section title="Page Background">
              <ColorPicker
                label="Page Background Color"
                value={userSettings.page?.backgroundColor}
                onChange={(v) => updateUserSetting('page', 'backgroundColor', v)}
              />
              <ColorPicker
                label="Default Text Color"
                value={userSettings.page?.textColor}
                onChange={(v) => updateUserSetting('page', 'textColor', v)}
              />
            </Section>

            {/* Accordion Header */}
            <Section title="Accordion Header">
              <ColorPicker
                label="Background Color"
                value={userSettings.accordionHeader?.backgroundColor}
                onChange={(v) => updateUserSetting('accordionHeader', 'backgroundColor', v)}
              />
              <ColorPicker
                label="Text Color"
                value={userSettings.accordionHeader?.textColor}
                onChange={(v) => updateUserSetting('accordionHeader', 'textColor', v)}
              />
              <ColorPicker
                label="Hover Color"
                value={userSettings.accordionHeader?.hoverColor}
                onChange={(v) => updateUserSetting('accordionHeader', 'hoverColor', v)}
              />
              <ColorPicker
                label="Border Color"
                value={userSettings.accordionHeader?.borderColor}
                onChange={(v) => updateUserSetting('accordionHeader', 'borderColor', v)}
              />
            </Section>

            {/* Welcome Box */}
            <Section title="Welcome Box">
              <ColorPicker
                label="Background Color"
                value={userSettings.welcomeBox?.backgroundColor}
                onChange={(v) => updateUserSetting('welcomeBox', 'backgroundColor', v)}
              />
              <ColorPicker
                label="Text Color"
                value={userSettings.welcomeBox?.textColor}
                onChange={(v) => updateUserSetting('welcomeBox', 'textColor', v)}
              />
              <ColorPicker
                label="Title Color"
                value={userSettings.welcomeBox?.titleColor}
                onChange={(v) => updateUserSetting('welcomeBox', 'titleColor', v)}
              />
              <ColorPicker
                label="Border Color"
                value={userSettings.welcomeBox?.borderColor}
                onChange={(v) => updateUserSetting('welcomeBox', 'borderColor', v)}
              />
            </Section>

            {/* Upgrade Box */}
            <Section title="Upgrade Box">
              <ColorPicker
                label="Background Color"
                value={userSettings.upgradeBox?.backgroundColor}
                onChange={(v) => updateUserSetting('upgradeBox', 'backgroundColor', v)}
              />
              <ColorPicker
                label="Text Color"
                value={userSettings.upgradeBox?.textColor}
                onChange={(v) => updateUserSetting('upgradeBox', 'textColor', v)}
              />
              <ColorPicker
                label="Title Color"
                value={userSettings.upgradeBox?.titleColor}
                onChange={(v) => updateUserSetting('upgradeBox', 'titleColor', v)}
              />
              <ColorPicker
                label="Border Color"
                value={userSettings.upgradeBox?.borderColor}
                onChange={(v) => updateUserSetting('upgradeBox', 'borderColor', v)}
              />
              <ColorPicker
                label="Button Color"
                value={userSettings.upgradeBox?.buttonColor}
                onChange={(v) => updateUserSetting('upgradeBox', 'buttonColor', v)}
              />
              <ColorPicker
                label="Button Text Color"
                value={userSettings.upgradeBox?.buttonTextColor}
                onChange={(v) => updateUserSetting('upgradeBox', 'buttonTextColor', v)}
              />
            </Section>

            {/* Verification Box */}
            <Section title="Verification Boxes">
              <ColorPicker
                label="Approved Background"
                value={userSettings.verificationBox?.backgroundColor}
                onChange={(v) => updateUserSetting('verificationBox', 'backgroundColor', v)}
              />
              <ColorPicker
                label="Approved Text Color"
                value={userSettings.verificationBox?.textColor}
                onChange={(v) => updateUserSetting('verificationBox', 'textColor', v)}
              />
              <ColorPicker
                label="Pending Background"
                value={userSettings.verificationBox?.pendingBackgroundColor}
                onChange={(v) => updateUserSetting('verificationBox', 'pendingBackgroundColor', v)}
              />
              <ColorPicker
                label="Pending Text Color"
                value={userSettings.verificationBox?.pendingTextColor}
                onChange={(v) => updateUserSetting('verificationBox', 'pendingTextColor', v)}
              />
              <ColorPicker
                label="Rejected Background"
                value={userSettings.verificationBox?.rejectedBackgroundColor}
                onChange={(v) => updateUserSetting('verificationBox', 'rejectedBackgroundColor', v)}
              />
              <ColorPicker
                label="Rejected Text Color"
                value={userSettings.verificationBox?.rejectedTextColor}
                onChange={(v) => updateUserSetting('verificationBox', 'rejectedTextColor', v)}
              />
            </Section>

            {/* Opportunities Box */}
            <Section title="Current Opportunities Box">
              <ColorPicker
                label="Background Color"
                value={userSettings.opportunitiesBox?.backgroundColor}
                onChange={(v) => updateUserSetting('opportunitiesBox', 'backgroundColor', v)}
              />
              <ColorPicker
                label="Text Color"
                value={userSettings.opportunitiesBox?.textColor}
                onChange={(v) => updateUserSetting('opportunitiesBox', 'textColor', v)}
              />
              <ColorPicker
                label="Title Color"
                value={userSettings.opportunitiesBox?.titleColor}
                onChange={(v) => updateUserSetting('opportunitiesBox', 'titleColor', v)}
              />
              <ColorPicker
                label="Border Color"
                value={userSettings.opportunitiesBox?.borderColor}
                onChange={(v) => updateUserSetting('opportunitiesBox', 'borderColor', v)}
              />
              <ColorPicker
                label="Badge Color"
                value={userSettings.opportunitiesBox?.badgeColor}
                onChange={(v) => updateUserSetting('opportunitiesBox', 'badgeColor', v)}
              />
            </Section>

            {/* Announcements Box */}
            <Section title="Announcements Box">
              <ColorPicker
                label="Background Color"
                value={userSettings.announcementsBox?.backgroundColor}
                onChange={(v) => updateUserSetting('announcementsBox', 'backgroundColor', v)}
              />
              <ColorPicker
                label="Text Color"
                value={userSettings.announcementsBox?.textColor}
                onChange={(v) => updateUserSetting('announcementsBox', 'textColor', v)}
              />
              <ColorPicker
                label="Title Color"
                value={userSettings.announcementsBox?.titleColor}
                onChange={(v) => updateUserSetting('announcementsBox', 'titleColor', v)}
              />
              <ColorPicker
                label="Border Color"
                value={userSettings.announcementsBox?.borderColor}
                onChange={(v) => updateUserSetting('announcementsBox', 'borderColor', v)}
              />
              <ColorPicker
                label="Link Color"
                value={userSettings.announcementsBox?.linkColor}
                onChange={(v) => updateUserSetting('announcementsBox', 'linkColor', v)}
              />
            </Section>

            {/* Quick Actions Box */}
            <Section title="Quick Actions Box">
              <ColorPicker
                label="Background Color"
                value={userSettings.quickActionsBox?.backgroundColor}
                onChange={(v) => updateUserSetting('quickActionsBox', 'backgroundColor', v)}
              />
              <ColorPicker
                label="Text Color"
                value={userSettings.quickActionsBox?.textColor}
                onChange={(v) => updateUserSetting('quickActionsBox', 'textColor', v)}
              />
              <ColorPicker
                label="Title Color"
                value={userSettings.quickActionsBox?.titleColor}
                onChange={(v) => updateUserSetting('quickActionsBox', 'titleColor', v)}
              />
              <ColorPicker
                label="Border Color"
                value={userSettings.quickActionsBox?.borderColor}
                onChange={(v) => updateUserSetting('quickActionsBox', 'borderColor', v)}
              />
              <ColorPicker
                label="Button Color"
                value={userSettings.quickActionsBox?.buttonColor}
                onChange={(v) => updateUserSetting('quickActionsBox', 'buttonColor', v)}
              />
              <ColorPicker
                label="Button Text Color"
                value={userSettings.quickActionsBox?.buttonTextColor}
                onChange={(v) => updateUserSetting('quickActionsBox', 'buttonTextColor', v)}
              />
            </Section>

            {/* Deals Box */}
            <Section title="Opportunities Page">
              <ColorPicker
                label="Card Background Color"
                value={userSettings.dealsBox?.backgroundColor}
                onChange={(v) => updateUserSetting('dealsBox', 'backgroundColor', v)}
              />
              <ColorPicker
                label="Card Header Background"
                value={userSettings.dealsBox?.headerBackgroundColor}
                onChange={(v) => updateUserSetting('dealsBox', 'headerBackgroundColor', v)}
              />
              <ColorPicker
                label="Text Color"
                value={userSettings.dealsBox?.textColor}
                onChange={(v) => updateUserSetting('dealsBox', 'textColor', v)}
              />
              <ColorPicker
                label="Title Color"
                value={userSettings.dealsBox?.titleColor}
                onChange={(v) => updateUserSetting('dealsBox', 'titleColor', v)}
              />
              <ColorPicker
                label="Border Color"
                value={userSettings.dealsBox?.borderColor}
                onChange={(v) => updateUserSetting('dealsBox', 'borderColor', v)}
              />
              <ColorPicker
                label="Price Color"
                value={userSettings.dealsBox?.priceColor}
                onChange={(v) => updateUserSetting('dealsBox', 'priceColor', v)}
              />
            </Section>

            {/* Support Box */}
            <Section title="Support Box">
              <ColorPicker
                label="Background Color"
                value={userSettings.supportBox?.backgroundColor}
                onChange={(v) => updateUserSetting('supportBox', 'backgroundColor', v)}
              />
              <ColorPicker
                label="Text Color"
                value={userSettings.supportBox?.textColor}
                onChange={(v) => updateUserSetting('supportBox', 'textColor', v)}
              />
              <ColorPicker
                label="Title Color"
                value={userSettings.supportBox?.titleColor}
                onChange={(v) => updateUserSetting('supportBox', 'titleColor', v)}
              />
              <ColorPicker
                label="Border Color"
                value={userSettings.supportBox?.borderColor}
                onChange={(v) => updateUserSetting('supportBox', 'borderColor', v)}
              />
              <ColorPicker
                label="Button Color"
                value={userSettings.supportBox?.buttonColor}
                onChange={(v) => updateUserSetting('supportBox', 'buttonColor', v)}
              />
            </Section>

            {/* Education Box */}
            <Section title="Education Box">
              <ColorPicker
                label="Background Color"
                value={userSettings.educationBox?.backgroundColor}
                onChange={(v) => updateUserSetting('educationBox', 'backgroundColor', v)}
              />
              <ColorPicker
                label="Text Color"
                value={userSettings.educationBox?.textColor}
                onChange={(v) => updateUserSetting('educationBox', 'textColor', v)}
              />
              <ColorPicker
                label="Title Color"
                value={userSettings.educationBox?.titleColor}
                onChange={(v) => updateUserSetting('educationBox', 'titleColor', v)}
              />
              <ColorPicker
                label="Border Color"
                value={userSettings.educationBox?.borderColor}
                onChange={(v) => updateUserSetting('educationBox', 'borderColor', v)}
              />
              <ColorPicker
                label="Play Button Color"
                value={userSettings.educationBox?.playButtonColor}
                onChange={(v) => updateUserSetting('educationBox', 'playButtonColor', v)}
              />
            </Section>

            {/* Requests Box */}
            <Section title="My Requests Box">
              <ColorPicker
                label="Background Color"
                value={userSettings.requestsBox?.backgroundColor}
                onChange={(v) => updateUserSetting('requestsBox', 'backgroundColor', v)}
              />
              <ColorPicker
                label="Text Color"
                value={userSettings.requestsBox?.textColor}
                onChange={(v) => updateUserSetting('requestsBox', 'textColor', v)}
              />
              <ColorPicker
                label="Title Color"
                value={userSettings.requestsBox?.titleColor}
                onChange={(v) => updateUserSetting('requestsBox', 'titleColor', v)}
              />
              <ColorPicker
                label="Border Color"
                value={userSettings.requestsBox?.borderColor}
                onChange={(v) => updateUserSetting('requestsBox', 'borderColor', v)}
              />
              <ColorPicker
                label="Approved Status Color"
                value={userSettings.requestsBox?.statusApprovedColor}
                onChange={(v) => updateUserSetting('requestsBox', 'statusApprovedColor', v)}
              />
              <ColorPicker
                label="Pending Status Color"
                value={userSettings.requestsBox?.statusPendingColor}
                onChange={(v) => updateUserSetting('requestsBox', 'statusPendingColor', v)}
              />
              <ColorPicker
                label="Rejected Status Color"
                value={userSettings.requestsBox?.statusRejectedColor}
                onChange={(v) => updateUserSetting('requestsBox', 'statusRejectedColor', v)}
              />
            </Section>

            {/* Dashboard Widget Headers */}
            <Section title="Dashboard Widget Headers">
              <ColorPicker
                label="Header Background Color"
                value={userSettings.dashboardWidgetHeader?.backgroundColor}
                onChange={(v) => updateUserSetting('dashboardWidgetHeader', 'backgroundColor', v)}
              />
              <p className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100">
                This color applies to the header section of all dashboard widgets (My Tasks, Commodity Tracker, News, Announcements, etc.)
              </p>
            </Section>

            {/* Quote Request Detail Page */}
            <Section title="Quote Request Detail Page">
              <ColorPicker
                label="Primary Color (Buttons, Progress)"
                value={userSettings.quoteDetail?.primaryColor}
                onChange={(v) => updateUserSetting('quoteDetail', 'primaryColor', v)}
              />
              <ColorPicker
                label="Secondary Color (Accents)"
                value={userSettings.quoteDetail?.secondaryColor}
                onChange={(v) => updateUserSetting('quoteDetail', 'secondaryColor', v)}
              />
              <ColorPicker
                label="Step Card Background"
                value={userSettings.quoteDetail?.stepCardBg}
                onChange={(v) => updateUserSetting('quoteDetail', 'stepCardBg', v)}
              />
              <ColorPicker
                label="Step Card Border"
                value={userSettings.quoteDetail?.stepCardBorder}
                onChange={(v) => updateUserSetting('quoteDetail', 'stepCardBorder', v)}
              />
              <ColorPicker
                label="Active Step Background"
                value={userSettings.quoteDetail?.activeStepBg}
                onChange={(v) => updateUserSetting('quoteDetail', 'activeStepBg', v)}
              />
              <ColorPicker
                label="Locked Step Text"
                value={userSettings.quoteDetail?.lockedStepText}
                onChange={(v) => updateUserSetting('quoteDetail', 'lockedStepText', v)}
              />
              <ColorPicker
                label="Not Started Header Background"
                value={userSettings.quoteDetail?.notStartedHeaderBg}
                onChange={(v) => updateUserSetting('quoteDetail', 'notStartedHeaderBg', v)}
              />
              <ColorPicker
                label="Pending Header Background"
                value={userSettings.quoteDetail?.pendingHeaderBg}
                onChange={(v) => updateUserSetting('quoteDetail', 'pendingHeaderBg', v)}
              />
              <ColorPicker
                label="Approved Header Background"
                value={userSettings.quoteDetail?.approvedHeaderBg}
                onChange={(v) => updateUserSetting('quoteDetail', 'approvedHeaderBg', v)}
              />
              <ColorPicker
                label="Denied Header Background"
                value={userSettings.quoteDetail?.deniedHeaderBg}
                onChange={(v) => updateUserSetting('quoteDetail', 'deniedHeaderBg', v)}
              />
            </Section>
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default AdminThemeSettings;
